import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2Icon, PlusIcon } from 'lucide-react'
import { adminFetch, type ManagedUser } from '../../lib/adminApi'
import { USER_ROLES } from '../../lib/permissions'

type TenantOption = { id: string; name: string; slug?: string; status?: string }

type OwnerForm = {
  first_name: string
  last_name: string
  email: string
  phone: string
  createMode: 'invite' | 'password'
  password: string
  passwordConfirm: string
}

type EditForm = {
  first_name: string
  last_name: string
  email: string
  phone: string
  role: string
  is_active: boolean
  tenant_id: string
}

const emptyOwner = (): OwnerForm => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  createMode: 'invite',
  password: '',
  passwordConfirm: '',
})

function roleLabel(role: string) {
  return USER_ROLES.find((r) => r.value === role)?.label || role
}

function displayRole(u: ManagedUser) {
  if (u.is_super_admin) return 'Super Admin'
  return roleLabel(u.role)
}

function companyName(u: ManagedUser) {
  if (u.is_super_admin) return 'Platform'
  return u.tenant?.name || '—'
}

function editFromUser(u: ManagedUser): EditForm {
  return {
    first_name: u.first_name || '',
    last_name: u.last_name || '',
    email: u.email || '',
    phone: u.phone || '',
    role: u.role || 'crew_member',
    is_active: u.is_active !== false,
    tenant_id: u.tenant_id || '',
  }
}

export function PlatformUsersPanel({
  session,
  canCreateSuperAdmins,
  tenants,
  onError,
}: {
  session: { access_token: string } | null
  canCreateSuperAdmins: boolean
  tenants: TenantOption[]
  onError: (msg: string | null) => void
}) {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [filterCompany, setFilterCompany] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [showCreateSa, setShowCreateSa] = useState(false)
  const [saForm, setSaForm] = useState<OwnerForm>(emptyOwner)
  const [allowCreateSa, setAllowCreateSa] = useState(false)
  const [editing, setEditing] = useState<ManagedUser | null>(null)
  const [editForm, setEditForm] = useState<EditForm | null>(null)

  const load = useCallback(async () => {
    if (!session) return
    setLoading(true)
    onError(null)
    try {
      const data = (await adminFetch('/api/admin/users?scope=platform', session)) as {
        users?: ManagedUser[]
      }
      setUsers(data.users || [])
    } catch (e: any) {
      onError(e.message || 'Failed to load users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [session, onError])

  useEffect(() => {
    load()
  }, [load])

  const companies = useMemo(() => {
    const map = new Map<string, string>()
    map.set('__platform__', 'Platform')
    for (const t of tenants) {
      map.set(t.id, t.name)
    }
    for (const u of users) {
      if (u.tenant_id && u.tenant?.name) map.set(u.tenant_id, u.tenant.name)
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [users, tenants])

  const roleOptions = useMemo(() => {
    const set = new Set<string>()
    for (const u of users) {
      set.add(u.is_super_admin ? '__super_admin__' : u.role || 'crew_member')
    }
    return Array.from(set)
  }, [users])

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (filterCompany) {
        if (filterCompany === '__platform__') {
          if (!u.is_super_admin) return false
        } else if (u.tenant_id !== filterCompany) {
          return false
        }
      }
      if (filterRole) {
        if (filterRole === '__super_admin__') {
          if (!u.is_super_admin) return false
        } else if (u.is_super_admin || u.role !== filterRole) {
          return false
        }
      }
      return true
    })
  }, [users, filterCompany, filterRole])

  const openEdit = (u: ManagedUser) => {
    setEditing(u)
    setEditForm(editFromUser(u))
    onError(null)
  }

  const closeEdit = () => {
    setEditing(null)
    setEditForm(null)
  }

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!session || !editing || !editForm) return
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()) {
      onError('First name, last name, and email are required')
      return
    }
    if (!editing.is_super_admin && !editForm.tenant_id) {
      onError('Company is required')
      return
    }
    setSaving(true)
    onError(null)
    try {
      const body: Record<string, unknown> = {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || null,
        is_active: editForm.is_active,
      }
      if (!editing.is_super_admin) {
        body.role = editForm.role
        body.tenant_id = editForm.tenant_id
      }
      await adminFetch('/api/admin/users/' + editing.id, session, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      closeEdit()
      await load()
    } catch (err: any) {
      onError(err.message || 'Failed to update user')
    } finally {
      setSaving(false)
    }
  }

  const createSuperAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!session || !canCreateSuperAdmins) return
    if (!saForm.first_name.trim() || !saForm.last_name.trim() || !saForm.email.trim()) {
      onError('First name, last name, and email are required')
      return
    }
    if (saForm.createMode === 'password') {
      if (saForm.password.length < 8) {
        onError('Password must be at least 8 characters')
        return
      }
      if (saForm.password !== saForm.passwordConfirm) {
        onError('Passwords do not match')
        return
      }
    }
    setSaving(true)
    onError(null)
    try {
      await adminFetch('/api/admin/users', session, {
        method: 'POST',
        body: JSON.stringify({
          email: saForm.email.trim(),
          first_name: saForm.first_name.trim(),
          last_name: saForm.last_name.trim(),
          phone: saForm.phone.trim() || null,
          role: 'admin',
          is_active: true,
          is_super_admin: true,
          can_create_super_admins: allowCreateSa,
          send_invite: saForm.createMode === 'invite',
          ...(saForm.createMode === 'password' ? { password: saForm.password, send_invite: false } : {}),
        }),
      })
      setShowCreateSa(false)
      setSaForm(emptyOwner())
      setAllowCreateSa(false)
      await load()
    } catch (err: any) {
      onError(err.message || 'Failed to create super admin')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <p className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${filtered.length} of ${users.length} user${users.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
          {canCreateSuperAdmins && (
            <button
              type="button"
              onClick={() => setShowCreateSa(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4" />
              New super admin
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-3 bg-gray-50">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
          <select
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white min-w-[10rem]"
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
          >
            <option value="">All companies</option>
            {companies.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
          <select
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white min-w-[10rem]"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="">All roles</option>
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {r === '__super_admin__' ? 'Super Admin' : roleLabel(r)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <Loader2Icon className="w-6 h-6 animate-spin mx-auto text-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">No users match these filters.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  className="align-top hover:bg-gray-50 cursor-pointer"
                  onClick={() => openEdit(u)}
                >
                  <td className="px-4 py-3 font-medium text-blue-700 hover:underline">
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3 text-gray-700">{companyName(u)}</td>
                  <td className="px-4 py-3">
                    <span className="text-gray-900">{displayRole(u)}</span>
                    {u.is_super_admin && u.can_create_super_admins && (
                      <div className="text-[10px] uppercase tracking-wide text-blue-700 mt-0.5">
                        Can create super admins
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        'text-xs font-semibold ' + (u.is_active !== false ? 'text-green-700' : 'text-gray-500')
                      }
                    >
                      {u.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900">
              Edit {editing.is_super_admin ? 'super admin' : 'user'}
            </h3>
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              {editing.is_super_admin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                    Platform (super admins are not assigned to a company)
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={editForm.tenant_id}
                      onChange={(e) => setEditForm({ ...editForm, tenant_id: e.target.value })}
                      required
                    >
                      <option value="">Select company…</option>
                      {tenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={editForm.role}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    >
                      {USER_ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                Active account
              </label>
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : null}
                  Save
                </button>
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateSa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">New super admin</h3>
            <p className="text-sm text-gray-500">
              Platform operators only. Super admins cannot be created from Settings → Team.
            </p>
            <form onSubmit={createSuperAdmin} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={saForm.first_name}
                    onChange={(e) => setSaForm({ ...saForm, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={saForm.last_name}
                    onChange={(e) => setSaForm({ ...saForm, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={saForm.email}
                  onChange={(e) => setSaForm({ ...saForm, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={saForm.phone}
                  onChange={(e) => setSaForm({ ...saForm, phone: e.target.value })}
                />
              </div>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={saForm.createMode === 'invite'}
                    onChange={() => setSaForm({ ...saForm, createMode: 'invite' })}
                  />
                  Send invite
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={saForm.createMode === 'password'}
                    onChange={() => setSaForm({ ...saForm, createMode: 'password' })}
                  />
                  Set password
                </label>
              </div>
              {saForm.createMode === 'password' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={saForm.password}
                      onChange={(e) => setSaForm({ ...saForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm</label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={saForm.passwordConfirm}
                      onChange={(e) => setSaForm({ ...saForm, passwordConfirm: e.target.value })}
                      required
                    />
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={allowCreateSa}
                  onChange={(e) => setAllowCreateSa(e.target.checked)}
                />
                Allow this user to create other super admins
              </label>
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : null}
                  Create super admin
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateSa(false)
                    setSaForm(emptyOwner())
                    setAllowCreateSa(false)
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
